import { useEffect, useMemo, useState } from "react";
import { File as FileIcon, Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { fileFingerprint, isImageFilename } from "@/lib/kaiAttachments";
import type { AttachmentEntry } from "@/hooks/useKaiAttachments";

function formatFileSize(bytes: number): string {
  if (bytes < 1_000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${(bytes / 1_000).toFixed(1)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** Object URLs for local image previews, keyed by file fingerprint. */
function useImagePreviewUrls(
  entries: AttachmentEntry[],
): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const entryKey = useMemo(
    () => entries.map((entry) => fileFingerprint(entry.file)).join("\0"),
    [entries],
  );

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const entry of entries) {
      if (!isImageFilename(entry.file.name)) continue;
      next[fileFingerprint(entry.file)] = URL.createObjectURL(entry.file);
    }
    setUrls(next);
    return () => {
      for (const url of Object.values(next)) {
        URL.revokeObjectURL(url);
      }
    };
    // `entryKey` captures the identity of the tracked files.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryKey]);

  return urls;
}

interface KaiAttachmentTokensProps {
  entries: AttachmentEntry[];
  onRemove: (index: number) => void;
}

/**
 * File token chips for the Kai composer, shown above the textarea while there
 * is at least one selected file. Displays a local image thumbnail, a spinner
 * while uploading/extracting, and an error message on failure.
 */
export function KaiAttachmentTokens({
  entries,
  onRemove,
}: KaiAttachmentTokensProps) {
  const previewUrls = useImagePreviewUrls(entries);

  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 px-1 pb-2">
      {entries.map((entry, index) => {
        const fp = fileFingerprint(entry.file);
        const isImage = isImageFilename(entry.file.name);
        const previewUrl = previewUrls[fp];
        const isLoading = entry.status === "uploading";
        const isFailed = entry.status === "failed";

        return (
          <span
            key={fp}
            className={cn(
              "inline-flex max-w-[16rem] items-center gap-2 rounded-lg border py-1 pl-1.5 pr-1 text-xs",
              isFailed
                ? "border-destructive/50 bg-destructive/[0.06]"
                : "border-border/50 bg-foreground/[0.04]",
            )}
          >
            <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md bg-foreground/[0.06] text-muted-foreground">
              {isLoading ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : isImage && previewUrl ? (
                <img
                  src={previewUrl}
                  alt=""
                  className="size-full object-cover"
                />
              ) : (
                <FileIcon className="size-3.5" />
              )}
            </span>

            <span className="flex min-w-0 flex-col">
              <span className="truncate text-foreground" title={entry.file.name}>
                {entry.file.name}
              </span>
              {isFailed ? (
                <span className="truncate text-[10px] text-destructive">
                  {entry.error ?? "Failed to process"}
                </span>
              ) : (
                <span className="text-[10px] text-muted-foreground">
                  {formatFileSize(entry.file.size)}
                </span>
              )}
            </span>

            <button
              type="button"
              aria-label={`Remove ${entry.file.name}`}
              className="flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent/60 hover:text-foreground"
              onClick={() => onRemove(index)}
            >
              <X className="size-3" />
            </button>
          </span>
        );
      })}
    </div>
  );
}
